import {
  Card,
  IconButton,
  Typography,
  MenuItem,
  Box,
  Tooltip,
  Chip,
  AccordionDetails,
  AccordionSummary,
  Accordion,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteIcon from "@mui/icons-material/Delete";
import { useController } from "react-hook-form";
import StyledTextField from "../../../../components/shared/StyledTextField";
import { useState } from "react";

export default function ShipmentItemCard({
  item,
  itemNumber,
  addressOptions = [],
  locationOptions = [],
  onRemove,
  control,
  setValue,
}) {
  const isEquipment = !!item?.equipment_number;

  const {
    field: addressField,
    fieldState: { error: addressError },
  } = useController({
    name: `items[${itemNumber}].address_id`,
    control,
    rules: isEquipment ? { required: "Por favor, selecione um endereço." } : {},
  });

  const {
    field: locationField,
    fieldState: { error: locationError },
  } = useController({
    name: `items[${itemNumber}].location_id`,
    control,
  });

  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      sx={{
        p: 2,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* EQUIPMENT */}
      {isEquipment ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          {/* Linha principal (nome + chips + 2 selects + delete) */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              width: "100%",
              alignItems: "flex-end",
            }}
          >
            {/* Nome do equipamento + chips */}
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexGrow: 1,
                minWidth: "250px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Tooltip
                  title={item?.equipment_name}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Typography
                    variant="subtitle1"
                    color="#424242"
                    noWrap
                    fontWeight="bold"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {item?.equipment_name}
                  </Typography>
                </Tooltip>

                <Chip
                  label={item?.serial_number || "S/N"}
                  size="small"
                  sx={{ fontSize: 11, flexShrink: 0 }}
                />
              </Box>

              {/* Chips sobre o título */}
              <Box
                sx={{
                  position: "absolute",
                  top: -22,
                  left: -2,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Chip label="EQUIPAMENTO" size="small" sx={{ fontSize: 10 }} />
              </Box>
            </Box>

            {/* Endereço */}
            <Box sx={{ position: "relative", flex: 1, minWidth: 200 }}>
              <StyledTextField
                select
                label="Endereço"
                size="small"
                name={addressField.name}
                value={addressField.value ?? ""}
                onBlur={addressField.onBlur}
                onChange={(event) => {
                  addressField.onChange(event);
                  setValue(`items[${itemNumber}].location_id`, "", {
                    shouldDirty: true,
                  });
                }}
                error={!!addressError}
                sx={{
                  width: "100%",
                }}
              >
                {addressOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              {addressError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    top: -18,
                    left: 2,
                    fontSize: "0.70rem",
                  }}
                >
                  {addressError.message}
                </Typography>
              )}
            </Box>

            {/* Localização */}
            <Box sx={{ position: "relative", flex: 1, minWidth: 200 }}>
              <StyledTextField
                select
                label="Localização"
                size="small"
                name={locationField.name}
                value={locationField.value ?? ""}
                onBlur={locationField.onBlur}
                onChange={locationField.onChange}
                error={!!locationError}
                sx={{
                  width: "100%",
                }}
              >
                {locationOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              {locationError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    position: "absolute",
                    top: -18,
                    left: 2,
                    fontSize: "0.70rem",
                  }}
                >
                  {locationError.message}
                </Typography>
              )}
            </Box>

            <IconButton onClick={onRemove} color="error">
              <DeleteIcon color="error" />
            </IconButton>
          </Box>

          {/* Campo Detalhes */}
          <StyledTextField
            label="Detalhes"
            multiline
            size="small"
            rows={1}
            placeholder="Opcional"
          />
        </Box>
      ) : (
        /* VOLUME */
        <Box sx={{ flex: 1, width: "100%" }}>
          <Accordion
            disableGutters
            elevation={0}
            square
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
          >
            {/* Cabeçalho */}
            <AccordionSummary
              sx={{
                padding: 0,
                margin: 0,
                borderRadius: "14px",
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                  mt: 1,
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: 0,
                  mt: 1,
                },
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)", // ou "transparent" se quiser remover o efeito cinza padrão
                  cursor: "pointer",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -10,
                  left: -2,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <Chip label="VOLUME" size="small" sx={{ fontSize: 10 }} />
              </Box>

              {/* Ícone */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mr: 1,
                }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    transition: "0.2s",
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>

              {/* Linha principal */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* Nome */}
                <Box sx={{ minWidth: 120 }}>
                  <Tooltip title={item?.volume_name}>
                    <Typography
                      variant="subtitle1"
                      noWrap
                      fontWeight="bold"
                      color="#424242"
                    >
                      {item?.volume_name}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Chip de quantidade */}

                <Chip
                  label={`${item?.items?.length ?? 0} itens`}
                  size="small"
                />

                {/* Delete */}
                <IconButton onClick={onRemove} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </AccordionSummary>

            {/* Itens do volume */}
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {item?.items?.map((i, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.20)",
                      borderRadius: 1,
                      px: 2,
                      py: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Tooltip
                      title={`${i.product_item?.product?.name} | ${i.product_item?.category?.name} | ${i.product_item?.condition?.name}`}
                    >
                      <Typography
                        noWrap
                        sx={{ flex: 1, fontWeight: "bold", color: "#424242" }}
                      >
                        {i.product_item?.product?.name} |{" "}
                        {i.product_item?.category?.name} |{" "}
                        {i.product_item?.condition?.name}
                      </Typography>
                    </Tooltip>

                    {/* Quantidade */}
                    <Typography sx={{ fontWeight: "bold", color: "#424242" }}>
                      {i.quantity}x
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Card>
  );
}
